/**
 * usePartnerEditor — a partner/02-03 admin-szerkesztő read-view hook (D0 frozen seam #357).
 * A `getPartnerEditor` runtime-validál a `PartnerEditorReadSchema`-ra (seam-drift azonnal kiderül).
 * Minta: `usePackageAdmin` (#350).
 */
import { getPartnerEditor } from '@/api/partner-editor';
import type { PartnerEditorRead } from '@kgc/partners/partner-editor';
import { useCallback, useEffect, useState } from 'react';

interface UsePartnerEditorResult {
  partner: PartnerEditorRead | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePartnerEditor(id: string | undefined): UsePartnerEditorResult {
  const [partner, setPartner] = useState<PartnerEditorRead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPartner = useCallback(async () => {
    if (!id) {
      setPartner(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setPartner(await getPartnerEditor(id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch partner';
      setError(message);
      console.error('Error fetching partner editor:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchPartner();
  }, [fetchPartner]);

  return { partner, isLoading, error, refetch: fetchPartner };
}
